import axios from 'axios';
import { Task } from '../types/task';
import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.usesieve.com';

export class TaskService {
    private authService: AuthService;

    constructor(authService: AuthService) {
        this.authService = authService;
    }

    private getAuthHeaders() {
        const token = this.authService.getToken();
        if (!token) {
            throw new Error('No authentication token found');
        }
        return {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        };
    }

    async getWorkerTasks(): Promise<Task[]> {
        try {
            console.log('Fetching worker tasks from:', `${API_BASE_URL}/api/v1/worker/tasks`);
            
            const response = await axios.get(`${API_BASE_URL}/api/v1/worker/tasks`, {
                headers: this.getAuthHeaders(),
            });
            
            return response.data as Task[];
        } catch (error: any) {
            console.error('Error fetching worker tasks:', error);
            if (error.response?.status === 403) {
                throw new Error('Not authorized as worker');
            }
            throw new Error('Failed to fetch worker tasks');
        }
    }

    async reviewTask(taskId: string, response: any, confidence: number): Promise<void> {
        try {
            // Prepare the request payload
            const payload: any = {
                response,
                confidence
            };

            const result = await axios.post(
                `${API_BASE_URL}/api/v1/worker/tasks/${taskId}/review`,
                payload,
                {
                    headers: this.getAuthHeaders(),
                }
            );

            if (!result.data) {
                throw new Error('Failed to submit review');
            }
        } catch (error: any) {
            console.error('Error reviewing task:', error);
            throw new Error(error.response?.data?.message || 'Failed to submit review');
        }
    }

    async rejectTask(taskId: string, rejectionReason: string = 'instructed_to_reject'): Promise<void> {
        try {
            const result = await axios.post(
                `${API_BASE_URL}/api/v1/worker/tasks/${taskId}/reject`,
                { rejection_reason: rejectionReason },
                {
                    headers: this.getAuthHeaders(),
                }
            );

            if (!result.data) {
                throw new Error('Failed to reject task');
            }
        } catch (error: any) {
            console.error('Error rejecting task:', error);
            throw new Error(error.response?.data?.message || 'Failed to reject task');
        }
    }
    
    async rejectMultipleTasks(taskIds: string[], rejectionReason: string = 'instructed_to_reject'): Promise<{
        successful: string[];
        failed: string[];
    }> {
        const results = {
            successful: [] as string[],
            failed: [] as string[]
        };
        
        if (!taskIds || taskIds.length === 0) {
            return results;
        }

        // Verify authentication headers are available
        const headers = this.getAuthHeaders();
        if (!headers.Authorization) {
            console.error('Authentication token missing');
            // Mark all tasks as failed if not authenticated
            results.failed = [...taskIds];
            return results;
        }
        
        // Process tasks sequentially to avoid overwhelming the API
        // Debug: Log the rejection reason being used for all tasks
        console.log('TaskService: Using rejection reason for batch operation:', rejectionReason);
        
        for (const taskId of taskIds) {
            try {
                // Use direct API call with proper error handling instead of rejectTask
                // This gives us more control over the specific error handling for batch operations
                console.log(`Rejecting task ${taskId} with reason: ${rejectionReason}`);
                
                const response = await axios.post(
                    `${API_BASE_URL}/api/v1/worker/tasks/${taskId}/reject`,
                    { rejection_reason: rejectionReason },
                    { headers }
                );
                
                if (response.status >= 200 && response.status < 300) {
                    results.successful.push(taskId);
                } else {
                    console.error(`Unexpected status code ${response.status} for task ${taskId}`);
                    results.failed.push(taskId);
                }
            } catch (error: any) {
                console.error(`Error rejecting task ${taskId}:`, error?.response?.status || error);
                results.failed.push(taskId);
                
                // If we get an authentication error, stop processing further tasks
                if (error?.response?.status === 401 || error?.response?.status === 403) {
                    console.error('Authentication error, stopping batch processing');
                    // Add all remaining tasks to failed list
                    const remainingTasks = taskIds.slice(taskIds.indexOf(taskId) + 1);
                    results.failed.push(...remainingTasks);
                    break;
                }
            }
        }
        
        return results;
    }
}
